import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SelectYN = ({ value, onChange, testid, options = ['Yes', 'No'] }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white">
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const WorkerProfileDialog = ({ staffData, token, apiCall }) => {
  const saved = staffData.profile?.worker_profile_extras || {};
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    gender: saved.gender || 'Male',
    enhanced_dbs: saved.enhanced_dbs || 'Yes',
    dbs_additional_info: saved.dbs_additional_info || 'Nil',
    worked_in_care_before: saved.worked_in_care_before || 'Yes',
    medical_adjustments: saved.medical_adjustments || 'No',
    rtw_restrictions: saved.rtw_restrictions || 'No',
  });
  const [photoB64, setPhotoB64] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await apiCall(`/admin/staff/${staffData.user.id}/worker-profile`, 'POST',
        { ...form, photo_base64: photoB64 }, token);
      toast.success('Worker profile PDF generated');
      window.open(`${BACKEND_URL}${res.pdf_url}?token=${token}`, '_blank');
      setOpen(false);
    } catch (e) {
      toast.error(e.detail || 'Failed to generate worker profile', { duration: 8000 });
    }
    setBusy(false);
  };

  return (
    <>
      <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
        onClick={() => setOpen(true)} data-testid={`worker-profile-btn-${staffData.user.id}`}>
        📄 Worker Profile PDF
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agency Worker Profile — {staffData.user.name}</DialogTitle>
            <DialogDescription>
              Name, DOB, job role, NMC, DBS details, reference check and the training table are pulled from the
              staff profile automatically. Confirm the remaining details below — if any profile data is missing,
              you'll be told exactly what to complete first.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Male or Female</Label>
              <SelectYN value={form.gender} onChange={(v) => setForm({ ...form, gender: v })}
                options={['Male', 'Female']} testid="wp-gender" />
            </div>
            <div>
              <Label>Enhanced DBS</Label>
              <SelectYN value={form.enhanced_dbs} onChange={(v) => setForm({ ...form, enhanced_dbs: v })} testid="wp-enhanced-dbs" />
            </div>
            <div className="col-span-2">
              <Label>Does the DBS contain additional information?</Label>
              <Input value={form.dbs_additional_info} onChange={(e) => setForm({ ...form, dbs_additional_info: e.target.value })} data-testid="wp-dbs-info" />
            </div>
            <div>
              <Label>Worked in a care setting before</Label>
              <SelectYN value={form.worked_in_care_before} onChange={(v) => setForm({ ...form, worked_in_care_before: v })} testid="wp-care-before" />
            </div>
            <div>
              <Label>Right-to-work restrictions</Label>
              <Input value={form.rtw_restrictions} onChange={(e) => setForm({ ...form, rtw_restrictions: e.target.value })} placeholder="e.g. No - British Citizen" data-testid="wp-restrictions" />
            </div>
            <div className="col-span-2">
              <Label>Medical adjustments needed for this role</Label>
              <Input value={form.medical_adjustments} onChange={(e) => setForm({ ...form, medical_adjustments: e.target.value })} data-testid="wp-medical" />
            </div>
            <div className="col-span-2">
              <Label>Photo {saved.photo_path ? '(saved photo will be reused unless you choose a new one)' : '(optional)'}</Label>
              <Input type="file" accept="image/jpeg,image/png" data-testid="wp-photo"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) { setPhotoB64(null); return; }
                  if (file.size > 3 * 1024 * 1024) { toast.error('Photo must be under 3MB'); e.target.value = ''; return; }
                  const reader = new FileReader();
                  reader.onload = () => setPhotoB64(reader.result);
                  reader.readAsDataURL(file);
                }} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={generate} data-testid="wp-generate-btn">
              {busy ? 'Generating…' : 'Generate PDF'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
